(() => {
  const HEADER_SELECTOR = '[data-ladona-premium-header]';
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'details > summary',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  class LadonaPremiumHeader {
    constructor(root) {
      if (!root || root.dataset.ladonaReady === 'true') return;

      this.root = root;
      this.megaToggle = root.querySelector('[data-ladona-mega-toggle]');
      this.mega = root.querySelector('[data-ladona-mega]');
      this.drawer = root.querySelector('[data-ladona-drawer]');
      this.drawerOpen = root.querySelector('[data-ladona-drawer-open]');
      this.drawerCloseButtons = root.querySelectorAll('[data-ladona-drawer-close]');
      this.lastFocusedElement = null;

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);

      this.bindEvents();
      this.onScroll();
      root.dataset.ladonaReady = 'true';
    }

    bindEvents() {
      this.megaToggle?.addEventListener('click', () => this.toggleMega());
      this.drawerOpen?.addEventListener('click', () => this.openDrawer());

      this.drawerCloseButtons.forEach((button) => {
        button.addEventListener('click', () => this.closeDrawer());
      });

      this.drawer?.querySelectorAll('a[href]').forEach((link) => {
        link.addEventListener('click', () => this.closeDrawer(false));
      });

      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
    }

    toggleMega() {
      if (!this.mega || !this.megaToggle) return;

      if (this.mega.hidden) {
        this.openMega();
      } else {
        this.closeMega();
      }
    }

    openMega() {
      if (!this.mega || !this.megaToggle) return;

      this.mega.hidden = false;
      this.megaToggle.setAttribute('aria-expanded', 'true');
    }

    closeMega(restoreFocus = false) {
      if (!this.mega || !this.megaToggle || this.mega.hidden) return;

      this.mega.hidden = true;
      this.megaToggle.setAttribute('aria-expanded', 'false');
      if (restoreFocus) this.megaToggle.focus();
    }

    openDrawer() {
      if (!this.drawer || !this.drawerOpen) return;

      this.closeMega();
      this.lastFocusedElement = document.activeElement;
      this.drawer.hidden = false;
      this.drawer.setAttribute('aria-hidden', 'false');
      this.drawerOpen.setAttribute('aria-expanded', 'true');
      document.body.classList.add('ladona-drawer-open');

      const firstFocusTarget =
        this.drawer.querySelector('[data-ladona-drawer-initial]') || this.drawer.querySelector(FOCUSABLE_SELECTOR);
      window.requestAnimationFrame(() => firstFocusTarget?.focus());
    }

    closeDrawer(restoreFocus = true) {
      if (!this.drawer || !this.drawerOpen || this.drawer.hidden) return;

      this.drawer.hidden = true;
      this.drawer.setAttribute('aria-hidden', 'true');
      this.drawerOpen.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('ladona-drawer-open');

      if (restoreFocus) {
        const focusTarget = this.lastFocusedElement || this.drawerOpen;
        focusTarget?.focus();
      }
    }

    trapDrawerFocus(event) {
      if (!this.drawer || this.drawer.hidden || event.key !== 'Tab') return;

      const focusable = [...this.drawer.querySelectorAll(FOCUSABLE_SELECTOR)].filter(
        (element) => !element.hasAttribute('hidden') && element.offsetParent !== null
      );

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onDocumentClick(event) {
      if (!this.root.contains(event.target)) this.closeMega();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        if (this.drawer && !this.drawer.hidden) {
          this.closeDrawer();
          return;
        }

        if (this.mega && !this.mega.hidden) this.closeMega(true);
      }

      this.trapDrawerFocus(event);
    }

    onScroll() {
      this.root.classList.toggle('is-scrolled', window.scrollY > 12);
    }

    onResize() {
      if (window.matchMedia('(min-width: 990px)').matches) {
        this.closeDrawer(false);
      } else {
        this.closeMega();
      }
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll(HEADER_SELECTOR).forEach((header) => new LadonaPremiumHeader(header));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
